import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyEntityNote, Scale } from '@tunasong/schemas'
import { useEffect, useRef, useState } from 'react'
import { spokenText } from '../theory-copy/spoken-text.js'
import { DegreeNumber } from './degree-number.js'
import type { FC, KeyboardEvent, MouseEvent, PointerEvent } from 'react'
import { applyMelodyDrag } from './melody-drag.js'
import type { MelodyDrag, MelodyDragKind } from './melody-drag.js'
import styles from './melody-roll.module.css'
import {
  beatX,
  isInKey,
  isTonic,
  pointToCell,
  rollBeats,
  rollPitchRange,
  rowCount,
  rowHeight,
  rowY,
  xToBeat,
} from './melody-roll-geometry.js'
import type { RollRange } from './melody-roll-geometry.js'

/**
 * The melody piano roll.
 *
 * Every mutation goes through a pure `MelodyLib` edit op and comes back out through `onChange`,
 * so the §2 invariants - grid-aligned onsets, ordered, non-overlapping, offsets bounded to half a
 * slot - are enforced in one tested place rather than in pointer handlers. The component owns
 * nothing but the drawing.
 *
 * A note is drawn at `onset + offsetMs / beatMs` and anchored at `onset`: the offset moves where
 * the bar SITS, never which slot it belongs to. That is the whole visual claim of this entity -
 * you can see the feel against the gridline.
 *
 * Without `onChange` the roll is read-only: no insert surface, no keyboard, no handles. That is
 * the mode the block render uses.
 *
 * Joni owns the visual design; v1 ships a functional default on design tokens.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface MelodyRollProps {
  melody: Melody
  /** Omit for a read-only roll. */
  onChange?: (melody: Melody) => void
  /** Draw the notes flat, with `offsetMs` treated as zero. */
  quantized?: boolean
  /** ms from the start of playback, or null/undefined when stopped. */
  playheadMs?: number | null
  /** The insert cursor, in beats. */
  cursorBeats?: number
  /** Called after a click-insert so the cursor follows the note that was just placed. */
  onCursorChange?: (beats: number) => void
  /** The focused note: its why line shows */
  selectedIndex?: number | null
  onSelect?: (index: number | null) => void
  /**
   * The selected notes, by index (melody toolkit M6; D7): a click selects one, Ctrl/Cmd-click toggles one, Shift+←/→
   * extends from the focused note, a drag of more than 4 px on the empty roll selects by box, and on a phone a
   * long-press starts a selection that taps add to or take from. Without `onSelectionChange`, only one note selects.
   */
  selection?: number[]
  onSelectionChange?: (indices: number[]) => void
  /** Render at block height rather than editor height. */
  compact?: boolean
  /**
   * False for a picture of a melody, such as the phrase picker's thumbnail (Phase 2 plan M5 step 5.2):
   * notes are not buttons or tab stops, so an `aria-hidden` wrapper hides nothing focusable.
   */
  interactive?: boolean
  /**
   * Notes to mark, by index, each with its label: Phase 2's feedback roll marks the notes that were not on
   * (plan M6a step 6.4). Drawn by shape and the label, never colour alone (Joni §7).
   */
  marks?: Record<number, string>
  /** Beats to draw at least: the chord lane's length, so every chord has its bars under it (toolkit M1) */
  minBeats?: number
  /** The keys the editor answers to while the roll has focus, for `aria-keyshortcuts` (spec §10) */
  keyShortcuts?: string
  /**
   * Theory cues (melody toolkit M3; spec §5.3): each note's roles over the chords under it. The note is
   * drawn by its role at its onset, by shape as well as colour; every role goes into its accessible name.
   */
  roles?: MelodyLib.MelodyNoteRole[]
  /** The key the cues are read in, for the accessible names of scale degrees */
  cueKey?: Scale
  /** Shade the strong beats: beat 1, and the bar's midpoint in even meters */
  isShadingStrongBeats?: boolean
  /** D8 (Bowie): draw each note's degree number on it, when it fits; never truncated */
  isShowingDegreeNumbers?: boolean
  /** "What fits here": while a note is dragged, how each pitch class fits the chord at a beat */
  rowFits?: (beat: number) => Map<number, MelodyLib.RowFit>
  /**
   * A ghost option (melody toolkit M4; spec §6): notes drawn outlined at half strength over the melody, with their own
   * theory cues. Never buttons or tab stops: the ghost bar's live region announces the option.
   */
  ghost?: MelodyRollGhost
  /**
   * The singer's range (melody toolkit M8a; spec §5.7), drawn as a band behind the notes, inclusive, with its label:
   * "Your range", or "Approximate range for {voice}". A picture: the editor says what it means in words.
   */
  rangeBand?: { low: number; high: number; label: string }
  /**
   * The written notes are view-only (Joni's M4 audit, approved by Brian): no insert, drag, resize or key edit, while
   * the roll keeps its focus and its shortcuts. The editor locks it while a ghost option shows.
   */
  isLocked?: boolean
  /** The id of what describes the roll group: the ghost bar's live region (Joni's M4 audit) */
  describedBy?: string
  className?: string
}

export interface MelodyRollGhost {
  notes: MelodyEntityNote[]
  /** The ghost notes' roles, by their own indexes */
  roles?: MelodyLib.MelodyNoteRole[]
  /** Fade in when they appear; false with reduced motion (spec §10) */
  isFading?: boolean
  /**
   * The written notes Keep would change or remove, by index (melody toolkit M6): only these are dimmed under the ghost.
   * Absent, every written note is dimmed, as for Suggest a melody's whole-line options.
   */
  dimmed?: number[]
}

interface NoteBox {
  x: number
  y: number
  width: number
  barHeight: number
}

/**
 * The cue shapes that carry a role without colour (WCAG 1.4.1): a stripe for a tension, an arrow for where a
 * suspension, lean or avoid note goes, a notch for a blue note. `testIdPrefix` keeps ghost cues apart in tests.
 */
const cueShapes = (role: MelodyLib.MelodyNoteRole | undefined, box: NoteBox, testIdPrefix = '') => {
  const { x, y, width, barHeight } = box
  const arrowWidth = Math.min(width * 0.35, 1.6)
  const arrowX = x + width - arrowWidth - Math.min(0.8, width / 4)
  const arrowTop = y + barHeight * 0.2
  const arrowBottom = y + barHeight * 0.8
  return (
    <>
      {role?.role === 'tension' ? (
        <rect
          data-testid={`${testIdPrefix}cue-stripe`}
          className={styles.cueStripe}
          x={x}
          y={y + barHeight * 0.4}
          width={width}
          height={barHeight * 0.2}
        />
      ) : null}
      {role?.target ? (
        <path
          data-testid={`${testIdPrefix}cue-arrow`}
          data-direction={role.target.direction}
          className={styles.cueArrow}
          d={
            role.target.direction === 'falls'
              ? `M ${arrowX} ${arrowTop} L ${arrowX + arrowWidth} ${arrowTop} L ${arrowX + arrowWidth / 2} ${arrowBottom} Z`
              : `M ${arrowX} ${arrowBottom} L ${arrowX + arrowWidth} ${arrowBottom} L ${arrowX + arrowWidth / 2} ${arrowTop} Z`
          }
        />
      ) : null}
      {role?.role === 'blue' ? (
        <path
          data-testid={`${testIdPrefix}cue-notch`}
          className={styles.cueNotch}
          d={`M ${x} ${y} L ${x + Math.min(width * 0.35, 1.2)} ${y} L ${x} ${y + barHeight * 0.7} Z`}
        />
      ) : null}
    </>
  )
}

/** Roles drawn by colour; the shape layers below carry the same role without it (WCAG 1.4.1) */
const ROLE_CLASS: Record<MelodyLib.NoteRoleKind, string | undefined> = {
  'chord-tone': styles.roleChordTone,
  tension: styles.roleChordTone,
  suspension: styles.roleChordTone,
  leans: styles.roleChordTone,
  avoid: styles.roleChordTone,
  passing: styles.rolePassing,
  neighbor: styles.rolePassing,
  blue: styles.roleBlue,
  outside: styles.roleOutside,
  'scale-degree': styles.roleChordTone,
}

/**
 * The same role colours on a ghost note's dashed outline, since a ghost has no fill (Joni's re-check; spec §6: the cues
 * apply to ghost notes too). Outside keeps the plain outline; its cue is that it has none of these colours.
 */
const GHOST_ROLE_CLASS: Record<MelodyLib.NoteRoleKind, string | undefined> = {
  'chord-tone': styles.ghostChordTone,
  tension: styles.ghostChordTone,
  suspension: styles.ghostChordTone,
  leans: styles.ghostChordTone,
  avoid: styles.ghostChordTone,
  passing: styles.ghostPassing,
  neighbor: styles.ghostPassing,
  blue: styles.ghostBlue,
  outside: undefined,
  'scale-degree': styles.ghostChordTone,
}

/**
 * A degree number's chip (Joni, M1–M5 review): this many pixels per character, its padding, and its height
 * (caption text and padding). It is drawn only inside its own row, so chips on neighbouring rows never
 * overlap, and only with some of the bar's role color left beside it.
 */
const DEGREE_CHAR_PX = 8
const DEGREE_CHIP_PADDING_PX = 8
const DEGREE_CHIP_HEIGHT_PX = 20
const DEGREE_FILL_PX = 8

const FIT_CLASS: Record<MelodyLib.RowFit, string | undefined> = {
  chord: styles.fitChord,
  tension: styles.fitTension,
  avoid: styles.fitAvoid,
}

/** Bar height as a fraction of its pitch row - a gap top and bottom keeps neighbours legible. */
const BAR_FILL = 0.7

/** Velocity as opacity, floored so a quiet note is never invisible. */
const noteOpacity = (velocity: number): number => 0.35 + 0.65 * velocity

/**
 * Where the bar is DRAWN, in beats.
 *
 * The swing displacement comes from `MelodyLib.swingDisplacementMs` - the same helper
 * `scheduleMelody` uses - rather than from a second copy of the arithmetic here. The roll is the
 * surface a musician edits the feel on, so a picture that disagreed with the sound would be
 * misreporting the one thing this editor exists for; two copies of the rule is how that
 * disagreement gets in.
 *
 * `quantized` zeroes the swing as well as the offset, matching the toggle's meaning in playback:
 * both are feel.
 */
const drawnOnset = (note: MelodyEntityNote, melody: Melody, quantized: boolean): number => {
  if (quantized) {
    return note.onset
  }
  const beatMs = MelodyLib.beatMs(melody.tempo.bpm)
  const swing = MelodyLib.swingDisplacementMs(note.onset, melody.feel?.swing ?? 0, beatMs)
  return note.onset + (swing + note.offsetMs) / beatMs
}

/**
 * A plain union rather than an `as const` array: nothing iterates the kinds at runtime, so an
 * array would be a value that only ever gets used as a type.
 */
type RowKind = 'tonic' | 'in-key' | 'out-of-key'

const rowKind = (midi: number, melody: Melody): RowKind => {
  if (isTonic(midi, melody.key)) {
    return 'tonic'
  }
  return isInKey(midi, melody.key) ? 'in-key' : 'out-of-key'
}

/** D7: a box select starts only after a drag this far from where the pointer went down, so a click still inserts */
const BOX_SELECT_PX = 4
/** How long a finger rests on a note before a phone selection starts */
const LONG_PRESS_MS = 500
/** How far a finger can move and still be resting: past this it is a drag (Joni's #3013 review, P1) */
const TOUCH_SLOP_PX = 8

const ROW_CLASS: Record<RowKind, string> = {
  tonic: styles.tonicRow,
  'in-key': styles.inKeyRow,
  'out-of-key': styles.outOfKeyRow,
}

export const MelodyRoll: FC<MelodyRollProps> = props => {
  const {
    melody,
    onChange,
    quantized = false,
    playheadMs,
    cursorBeats,
    onCursorChange,
    selectedIndex,
    onSelect,
    selection = [],
    onSelectionChange,
    compact = false,
    interactive = true,
    marks,
    minBeats,
    keyShortcuts,
    roles = [],
    cueKey,
    isShadingStrongBeats = false,
    isShowingDegreeNumbers = false,
    rowFits,
    ghost,
    rangeBand,
    isLocked = false,
    describedBy,
    className,
  } = props
  /** Every edit goes through this: none while the roll is locked */
  const edit = isLocked ? undefined : onChange

  /** The ghost notes count for the pitch range and the length, so an option is never drawn off the roll */
  /** The band counts too, so the whole range is on the roll (spec §5.7) */
  const bandEdges = rangeBand
    ? [rangeBand.low, rangeBand.high].map(midi => ({ midi, onset: 0, duration: 0, velocity: 0, offsetMs: 0 }))
    : []
  const drawnMelody =
    ghost || rangeBand ? { ...melody, notes: [...melody.notes, ...(ghost?.notes ?? []), ...bandEdges] } : melody
  const range: RollRange = rollPitchRange(drawnMelody)
  const totalBeats = rollBeats(drawnMelody, minBeats)
  const slot = MelodyLib.gridSlotBeats(melody.grid)
  const perBar = MelodyLib.barBeats(melody.tempo.meter)
  const height = rowHeight(range)
  const rows = Array.from({ length: rowCount(range) }, (_, i) => range.high - i)
  const slotCount = Math.round(totalBeats / slot)

  /**
   * The drag in flight. A ref, not state: a pointer move must not re-render on its own, and the
   * melody the drag started from has to survive the re-renders `onChange` causes - otherwise each
   * move would compound against an already-moved note instead of the grab position.
   */
  const drag = useRef<{ drag: MelodyDrag; from: Melody } | null>(null)
  const plot = useRef<SVGSVGElement | null>(null)
  /** The beat under a note being dragged, for "what fits here"; null when nothing is */
  const [fitBeat, setFitBeat] = useState<number | null>(null)
  /** The beat of a focused note moved with ↑/↓, for the same cue; cleared when focus leaves it (Joni) */
  const [keyFitBeat, setKeyFitBeat] = useState<number | null>(null)
  /** The plot's size in pixels, so a degree number is drawn only where it fits */
  const [plotSize, setPlotSize] = useState({ width: 0, height: 0 })
  /** The note hovered or focused: its degree number shows even where there is no room for it */
  const [revealedIndex, setRevealedIndex] = useState<number | null>(null)
  /** Where a pointer went down on the empty roll: a box select once it moves more than 4 px, else a click */
  const boxStart = useRef<{ clientX: number; clientY: number } | null>(null)
  /** The box being dragged, in plot percent */
  const [box, setBox] = useState<{ from: { x: number; y: number }; to: { x: number; y: number } } | null>(null)
  /** The click that ends a box select, or a cancelled one, is not an insert */
  const isClickSuppressed = useRef(false)
  /** The note a long-press just selected: the click that ends the press is not a tap that takes it back out */
  const longPressedNote = useRef<number | null>(null)
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Where the long-press began, so a finger that moves past the slop drags instead */
  const pressAt = useRef<{ clientX: number; clientY: number } | null>(null)
  /** A phone selection, started by a long-press: taps add or take out notes, and a tap on an empty cell does nothing */
  const [isTouchSelecting, setTouchSelecting] = useState(false)

  useEffect(() => {
    const element = plot.current
    if (!element || typeof ResizeObserver === 'undefined' || !isShowingDegreeNumbers) {
      return undefined
    }
    const observer = new ResizeObserver(([entry]) =>
      setPlotSize({ width: entry?.contentRect.width ?? 0, height: entry?.contentRect.height ?? 0 })
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [isShowingDegreeNumbers])

  const rolesByNote = new Map<number, MelodyLib.MelodyNoteRole[]>()
  for (const role of roles) {
    rolesByNote.set(role.noteIndex, [...(rolesByNote.get(role.noteIndex) ?? []), role])
  }
  const shownFitBeat = fitBeat ?? keyFitBeat
  const fits = shownFitBeat === null || !rowFits ? null : rowFits(shownFitBeat)
  const beat = MelodyLib.feltBeat(melody.tempo.meter)
  const strongBeats = isShadingStrongBeats
    ? Array.from({ length: Math.round(totalBeats / perBar) }, (_, bar) => [
        bar * perBar,
        ...(melody.tempo.meter.top % 2 === 0 ? [bar * perBar + perBar / 2] : []),
      ]).flat()
    : []

  /** Pointer position as a percentage of the plot, or null when there is no laid-out plot yet. */
  const percentOf = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = plot.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) {
      return null
    }
    return { x: ((clientX - rect.left) / rect.width) * 100, y: ((clientY - rect.top) / rect.height) * 100 }
  }

  const handleInsert = (event: MouseEvent<SVGRectElement>) => {
    if (isClickSuppressed.current) {
      isClickSuppressed.current = false
      return
    }
    /** Joni's #3013 review, P2 3: while selecting on a phone, a tap on an empty cell clears the selection */
    if (isTouchSelecting) {
      setTouchSelecting(false)
      onSelectionChange?.([])
      return
    }
    if (!edit) {
      return
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    const { beat, midi } = pointToCell(at.x, at.y, range, totalBeats, melody.grid)
    edit(MelodyLib.insertMelodyNote(melody, { midi, onset: beat }))
    /* The cursor follows the note just placed, so keyboard and MIDI entry continue from here. */
    onCursorChange?.(beat + slot)
  }

  const cancelLongPress = () => {
    if (longPress.current !== null) {
      clearTimeout(longPress.current)
      longPress.current = null
    }
  }

  const handleGrab = (event: PointerEvent<SVGRectElement>, index: number, kind: MelodyDragKind) => {
    if (!edit) {
      return
    }
    if (event.pointerType === 'touch' && onSelectionChange) {
      cancelLongPress()
      longPressedNote.current = null
      pressAt.current = { clientX: event.clientX, clientY: event.clientY }
      longPress.current = setTimeout(() => {
        longPress.current = null
        drag.current = null
        setFitBeat(null)
        longPressedNote.current = index
        setTouchSelecting(true)
        onSelectionChange([index])
      }, LONG_PRESS_MS)
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { drag: { kind, index, startXPercent: at.x, startYPercent: at.y }, from: melody }
    setFitBeat(xToBeat(at.x, totalBeats))
    onSelect?.(index)
  }

  /** The notes a box covers, by index */
  const notesInBox = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const left = Math.min(from.x, to.x)
    const right = Math.max(from.x, to.x)
    const top = Math.min(from.y, to.y)
    const bottom = Math.max(from.y, to.y)
    return melody.notes.flatMap((note, index) => {
      const x = beatX(drawnOnset(note, melody, quantized), totalBeats)
      const y = rowY(note.midi, range)
      const isCovered = x < right && x + beatX(note.duration, totalBeats) > left && y < bottom && y + height > top
      return isCovered ? [index] : []
    })
  }

  const handleSurfaceDown = (event: PointerEvent<SVGRectElement>) => {
    if (onSelectionChange && event.pointerType !== 'touch') {
      boxStart.current = { clientX: event.clientX, clientY: event.clientY }
    }
  }

  const handleBoxMove = (event: PointerEvent<SVGElement>) => {
    const start = boxStart.current
    if (!start) {
      return
    }
    const moved = Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY)
    const from = percentOf(start.clientX, start.clientY)
    const to = percentOf(event.clientX, event.clientY)
    if (!from || !to || (box === null && moved <= BOX_SELECT_PX)) {
      return
    }
    setBox({ from, to })
  }

  const handleBoxEnd = () => {
    boxStart.current = null
    if (!box) {
      return
    }
    isClickSuppressed.current = true
    setBox(null)
    onSelectionChange?.(notesInBox(box.from, box.to))
  }

  /** Esc cancels a box select in flight; it says nothing otherwise, so the editor's Esc still works */
  const handlePlotKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'Escape' || (!box && !boxStart.current)) {
      return
    }
    event.stopPropagation()
    boxStart.current = null
    if (box) {
      isClickSuppressed.current = true
      setBox(null)
    }
  }

  const handleNoteClick = (event: MouseEvent<SVGGElement>, index: number) => {
    onSelect?.(index)
    if (!onSelectionChange) {
      return
    }
    const wasLongPressed = longPressedNote.current === index
    longPressedNote.current = null
    if (wasLongPressed) {
      return
    }
    if (event.ctrlKey || event.metaKey || isTouchSelecting) {
      const next = selection.includes(index)
        ? selection.filter(entry => entry !== index)
        : [...selection, index].sort((a, b) => a - b)
      if (next.length === 0) {
        setTouchSelecting(false)
      }
      onSelectionChange(next)
      return
    }
    onSelectionChange([index])
  }

  /** Shift+←/→: the selection grows by the next note that way, from the focused note, in the order the notes sound */
  const extendSelection = (index: number, isLater: boolean) => {
    if (!onSelectionChange) {
      return
    }
    const order = melody.notes
      .map((note, noteIndex) => ({ note, noteIndex }))
      .sort((a, b) => a.note.onset - b.note.onset || a.noteIndex - b.noteIndex)
      .map(entry => entry.noteIndex)
    const current = new Set([...selection, index])
    const positions = order.flatMap((noteIndex, position) => (current.has(noteIndex) ? [position] : []))
    const next = order[isLater ? Math.max(...positions) + 1 : Math.min(...positions) - 1]
    onSelectionChange([...current, ...(next === undefined ? [] : [next])].sort((a, b) => a - b))
  }

  const handleDragMove = (event: PointerEvent<SVGElement>) => {
    handleBoxMove(event)
    const press = pressAt.current
    if (
      longPress.current !== null &&
      press &&
      Math.hypot(event.clientX - press.clientX, event.clientY - press.clientY) > TOUCH_SLOP_PX
    ) {
      cancelLongPress()
    }
    const inFlight = drag.current
    if (!inFlight || !edit) {
      return
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    setFitBeat(xToBeat(at.x, totalBeats))
    edit(
      applyMelodyDrag(
        inFlight.from,
        inFlight.drag,
        at.x - inFlight.drag.startXPercent,
        at.y - inFlight.drag.startYPercent,
        { range, totalBeats }
      )
    )
  }

  const handleDragEnd = () => {
    cancelLongPress()
    handleBoxEnd()
    drag.current = null
    setFitBeat(null)
  }

  const handleNoteKeyDown = (event: KeyboardEvent<SVGGElement>, index: number) => {
    if (!edit) {
      return
    }
    const note = melody.notes[index]
    if (!note) {
      return
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()
      edit(MelodyLib.removeMelodyNote(melody, index))
      return
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      if (event.shiftKey) {
        extendSelection(index, event.key === 'ArrowRight')
        return
      }
      const step = event.key === 'ArrowRight' ? slot : -slot
      edit(
        event.altKey
          ? MelodyLib.resizeMelodyNote(melody, index, note.duration + step)
          : MelodyLib.moveMelodyNote(melody, index, { midi: note.midi, onset: note.onset + step })
      )
      return
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      const step = event.key === 'ArrowUp' ? 1 : -1
      setKeyFitBeat(note.onset)
      edit(MelodyLib.moveMelodyNote(melody, index, { midi: note.midi + step, onset: note.onset }))
    }
  }

  /** Under a ghost, the notes it would change; all of them when it doesn't say */
  const isDimmed = (index: number) => ghost !== undefined && (ghost.dimmed?.includes(index) ?? true)

  const playheadBeats = playheadMs == null ? null : playheadMs / MelodyLib.beatMs(melody.tempo.bpm)

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {rangeBand ? (
        /** Over the plot in HTML, like the marks, so the label keeps a readable size */
        <div className={styles.rangeLabelLayer} aria-hidden="true">
          <span className={styles.rangeLabel} style={{ top: `${rowY(rangeBand.high, range)}%` }}>
            {rangeBand.label}
          </span>
        </div>
      ) : null}
      {marks ? (
        /**
         * The labels sit over the plot in HTML, not in the stretched viewBox, so they keep a readable size
         * (Joni, M6a: at least 11 px). The plot is in percent, so a note's place is its percent too.
         */
        <div className={styles.markLayer} aria-hidden="true">
          {Object.entries(marks).flatMap(([index, label]) => {
            const note = melody.notes[Number(index)]
            if (!note) {
              return []
            }
            const left = beatX(drawnOnset(note, melody, quantized), totalBeats)
            const top = rowY(note.midi, range)
            return [
              <span
                key={index}
                data-testid="melody-note-mark"
                className={styles.markLabel}
                style={{ left: `${left}%`, top: `${top}%` }}
              >
                {label}
              </span>,
            ]
          })}
        </div>
      ) : null}
      {isShowingDegreeNumbers && plotSize.width > 0 ? (
        /** HTML over the stretched viewBox, like the marks, so the numbers keep their size */
        <div className={styles.degreeLayer} aria-hidden="true">
          {melody.notes.flatMap((note, index) => {
            const degreeNumber = rolesByNote.get(index)?.[0]?.degreeNumber
            const widthPx = (beatX(note.duration, totalBeats) / 100) * plotSize.width
            const rowPx = (height / 100) * plotSize.height
            const chipPx = degreeNumber ? degreeNumber.length * DEGREE_CHAR_PX + DEGREE_CHIP_PADDING_PX : 0
            const isRoomy = rowPx >= DEGREE_CHIP_HEIGHT_PX && widthPx >= chipPx + DEGREE_FILL_PX
            if (!degreeNumber || (!isRoomy && revealedIndex !== index)) {
              return []
            }
            return [
              <span
                key={index}
                data-testid="melody-note-degree"
                className={styles.degreeLabel}
                style={{
                  left: `${beatX(drawnOnset(note, melody, quantized), totalBeats)}%`,
                  top: `${rowY(note.midi, range)}%`,
                  height: `${height}%`,
                }}
              >
                <span>
                  <DegreeNumber value={degreeNumber} />
                </span>
              </span>,
            ]
          })}
        </div>
      ) : null}
      <svg
        ref={plot}
        className={compact ? `${styles.svg} ${styles.compact}` : styles.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="group"
        aria-label={`Melody piano roll, ${melody.tempo.bpm} bpm, ${melody.tempo.meter.top}/${melody.tempo.meter.bottom}`}
        data-beats={totalBeats}
        /** Melody toolkit M2, spec §10: the editor's shortcuts work while the roll has focus */
        tabIndex={onChange && interactive ? 0 : undefined}
        aria-keyshortcuts={onChange && interactive ? keyShortcuts : undefined}
        aria-describedby={describedBy}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        onKeyDown={handlePlotKeyDown}
      >
        {rows.map(midi => {
          const kind = rowKind(midi, melody)
          const fit = fits?.get(midi % 12)
          return (
            <rect
              key={`row-${midi}`}
              data-testid="melody-row"
              data-row={kind}
              data-midi={midi}
              data-fit={fit}
              className={[ROW_CLASS[kind], fit ? FIT_CLASS[fit] : null].filter(Boolean).join(' ')}
              x={0}
              y={rowY(midi, range)}
              width={100}
              height={height}
            />
          )
        })}

        {rangeBand ? (
          <g aria-hidden="true">
            <rect
              data-testid="melody-range-band"
              className={styles.rangeBand}
              x={0}
              y={rowY(rangeBand.high, range)}
              width={100}
              height={rowY(rangeBand.low, range) + height - rowY(rangeBand.high, range)}
            />
          </g>
        ) : null}

        {strongBeats.map(at => (
          <rect
            key={`strong-${at}`}
            data-testid="melody-strong-beat"
            data-beat={at}
            className={styles.strongBeat}
            x={beatX(at, totalBeats)}
            y={0}
            width={beatX(beat, totalBeats)}
            height={100}
          />
        ))}

        {Array.from({ length: slotCount + 1 }, (_, i) => {
          const beats = i * slot
          const isBar = Math.abs(beats % perBar) <= MelodyLib.GRID_TOLERANCE
          return (
            <line
              key={`slot-${i}`}
              data-testid={isBar ? 'melody-bar-line' : 'melody-slot-line'}
              className={isBar ? styles.barLine : styles.slotLine}
              x1={beatX(beats, totalBeats)}
              y1={0}
              x2={beatX(beats, totalBeats)}
              y2={100}
            />
          )
        })}

        {edit ? (
          <rect
            data-testid="melody-insert-surface"
            className={styles.insertSurface}
            x={0}
            y={0}
            width={100}
            height={100}
            onClick={handleInsert}
            onPointerDown={handleSurfaceDown}
          />
        ) : null}

        {melody.notes.map((note, index) => {
          const x = beatX(drawnOnset(note, melody, quantized), totalBeats)
          const width = beatX(note.duration, totalBeats)
          const y = rowY(note.midi, range) + (height * (1 - BAR_FILL)) / 2
          const barHeight = height * BAR_FILL
          /** With multi-select wired, the selection is what is drawn selected; else the one selected note */
          const selected = onSelectionChange ? selection.includes(index) : selectedIndex === index
          const mark = marks?.[index]
          /** Drawn by its role at its onset; a note held across a change names every role it has */
          const role = rolesByNote.get(index)?.[0]
          return (
            <g
              /* Not keyed by pitch: a note moved with ↑/↓ stays the same element, and keeps focus */
              key={`${note.onset}-${index}`}
              /** Dimmed under a ghost option, so the option reads as the thing on top (Joni's review) */
              className={isDimmed(index) ? `${styles.noteGroup} ${styles.noteDimmed}` : styles.noteGroup}
              data-dimmed={isDimmed(index) ? 'true' : undefined}
              {...(interactive
                ? {
                    role: 'button',
                    tabIndex: 0,
                    /** Joni's #3013 review, P2 2: which notes are in the selection is said, not only drawn */
                    ...(onSelectionChange ? { 'aria-pressed': selected } : {}),
                    /** Spoken in words: "E flat 4 … the flat 3", never "music flat sign" (Brian, 2026-10-23) */
                    'aria-label': spokenText(
                      MelodyLib.describeMelodyNote(note, melody, {
                        roles: rolesByNote.get(index) ?? [],
                        ...(cueKey ? { key: cueKey } : {}),
                      })
                    ),
                    onClick: (event: MouseEvent<SVGGElement>) => handleNoteClick(event, index),
                    /** Spec §5.3: focusing a note shows its why line, as selecting it does */
                    onFocus: () => {
                      onSelect?.(index)
                      setRevealedIndex(index)
                    },
                    onPointerEnter: () => setRevealedIndex(index),
                    onPointerLeave: () => setRevealedIndex(null),
                    onKeyDown: (event: KeyboardEvent<SVGGElement>) => handleNoteKeyDown(event, index),
                    onBlur: () => {
                      setKeyFitBeat(null)
                      setRevealedIndex(null)
                    },
                  }
                : {})}
            >
              <rect
                data-testid="melody-note"
                data-selected={selected ? 'true' : undefined}
                data-mark={mark}
                data-role={role?.role}
                className={[
                  styles.note,
                  role ? ROLE_CLASS[role.role] : null,
                  selected ? styles.noteSelected : null,
                  mark ? styles.noteMarked : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                x={x}
                y={y}
                width={width}
                height={barHeight}
                opacity={noteOpacity(note.velocity)}
                onPointerDown={event => handleGrab(event, index, 'move')}
              />
              {cueShapes(role, { x, y, width, barHeight })}
              {edit ? (
                <rect
                  data-testid="melody-note-offset"
                  className={styles.offsetHandle}
                  x={x}
                  y={y}
                  width={Math.min(0.8, width / 4)}
                  height={barHeight}
                  onPointerDown={event => handleGrab(event, index, 'offset')}
                />
              ) : null}
              {edit ? (
                <rect
                  data-testid="melody-note-resize"
                  className={styles.resizeHandle}
                  x={x + width - Math.min(0.8, width / 4)}
                  y={y}
                  width={Math.min(0.8, width / 4)}
                  height={barHeight}
                  onPointerDown={event => handleGrab(event, index, 'resize')}
                />
              ) : null}
            </g>
          )
        })}

        {ghost ? (
          <g data-testid="melody-ghost" aria-hidden="true" className={styles.ghostLayer}>
            {ghost.notes.map((note, index) => {
              const box = {
                x: beatX(note.onset, totalBeats),
                y: rowY(note.midi, range) + (height * (1 - BAR_FILL)) / 2,
                width: beatX(note.duration, totalBeats),
                barHeight: height * BAR_FILL,
              }
              const role = ghost.roles?.find(entry => entry.noteIndex === index)
              return (
                /**
                 * Joni's review: the note and its cue shapes share the group's half strength and its fade. The note is
                 * an outline, never filled like a written note; its role is carried by the cue shapes.
                 */
                <g
                  key={`ghost-${note.onset}-${index}`}
                  data-testid="melody-ghost-group"
                  data-fading={ghost.isFading ? 'true' : undefined}
                  className={ghost.isFading ? `${styles.ghostGroup} ${styles.ghostFade}` : styles.ghostGroup}
                >
                  <rect
                    data-testid="melody-ghost-note"
                    data-role={role?.role}
                    className={[styles.ghostNote, role ? GHOST_ROLE_CLASS[role.role] : null].filter(Boolean).join(' ')}
                    x={box.x}
                    y={box.y}
                    width={box.width}
                    height={box.barHeight}
                  />
                  {cueShapes(role, box, 'ghost-')}
                </g>
              )
            })}
          </g>
        ) : null}

        {box ? (
          <rect
            data-testid="melody-box-select"
            className={styles.boxSelect}
            x={Math.min(box.from.x, box.to.x)}
            y={Math.min(box.from.y, box.to.y)}
            width={Math.abs(box.to.x - box.from.x)}
            height={Math.abs(box.to.y - box.from.y)}
          />
        ) : null}

        {playheadBeats == null ? null : (
          <line
            data-testid="melody-playhead"
            className={styles.playhead}
            x1={beatX(playheadBeats, totalBeats)}
            y1={0}
            x2={beatX(playheadBeats, totalBeats)}
            y2={100}
          />
        )}

        {cursorBeats == null || !onChange ? null : (
          <line
            data-testid="melody-cursor"
            className={styles.cursor}
            x1={beatX(cursorBeats, totalBeats)}
            y1={0}
            x2={beatX(cursorBeats, totalBeats)}
            y2={100}
          />
        )}
      </svg>
    </div>
  )
}
